import { useState } from "react";
import axios from "axios";
import { X, Loader2, CheckCircle, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFormPopup } from "@/components/FormPopupContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_URL = "https://api.whatsapp.com/send?phone=+18158599201&text=987E5A";

const montoOptions = [
  "$500.000",
  "$1.000.000",
  "$2.000.000",
  "$3.000.000",
  "$5.000.000",
  "$10.000.000",
  "$15.000.000",
  "$20.000.000",
];

export default function LeadFormPopup() {
  const { open, setOpen } = useFormPopup();
  const [nombre, setNombre] = useState("");
  const [rut, setRut] = useState("");
  const [telefono, setTelefono] = useState("");
  const [correo, setCorreo] = useState("");
  const [monto, setMonto] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!nombre.trim() || !rut.trim() || !telefono.trim() || !correo.trim() || !monto) {
      setError("Por favor completa todos los campos.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/lead-form`, {
        nombre: nombre.trim(),
        rut: rut.trim(),
        telefono: telefono.trim(),
        correo: correo.trim(),
        monto,
      });

      if (window.gtag_report_conversion) window.gtag_report_conversion();

      setOpen(false);
      setNombre(""); setRut(""); setTelefono(""); setCorreo(""); setMonto("");
      window.open(WHATSAPP_URL, "_blank");
    } catch (err) {
      setError("Ocurrio un error. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="lead-form-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
      style={{ background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div
        data-testid="lead-form-popup"
        className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-fade-in-up"
      >
        <button
          data-testid="lead-form-close"
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors z-10"
        >
          <X className="w-4 h-4 text-slate-500" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-t-3xl px-8 pt-8 pb-6">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center mb-4">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <h2
            className="text-xl font-semibold text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Solicita tu credito
          </h2>
          <p className="text-sm text-emerald-100 mt-1">
            Completa tus datos y te contactaremos de inmediato.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-4">
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Nombre completo</Label>
            <Input
              data-testid="lead-form-nombre"
              type="text"
              placeholder="Juan Perez"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="rounded-xl bg-slate-50 border-slate-200 h-11"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">RUT</Label>
            <Input
              data-testid="lead-form-rut"
              type="text"
              placeholder="12.345.678-9"
              value={rut}
              onChange={(e) => setRut(e.target.value)}
              className="rounded-xl bg-slate-50 border-slate-200 h-11"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Telefono</Label>
            <Input
              data-testid="lead-form-telefono"
              type="tel"
              placeholder="+56 9 1234 5678"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              className="rounded-xl bg-slate-50 border-slate-200 h-11"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Correo electronico</Label>
            <Input
              data-testid="lead-form-correo"
              type="email"
              placeholder="tu@email.com"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              className="rounded-xl bg-slate-50 border-slate-200 h-11"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Monto a solicitar</Label>
            <div className="grid grid-cols-2 gap-2">
              {montoOptions.map((m) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`lead-form-monto-${m}`}
                  onClick={() => setMonto(m)}
                  className={`text-sm font-medium rounded-xl px-3 py-2.5 border transition-all duration-200 ${
                    monto === m
                      ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                      : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <p data-testid="lead-form-error" className="text-sm text-red-500">{error}</p>
          )}

          <Button
            type="submit"
            data-testid="lead-form-submit"
            disabled={loading}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-xl h-12 text-base shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/30 transition-all duration-200 active:scale-95 disabled:opacity-60 mt-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-1" />
                Enviar solicitud
              </>
            )}
          </Button>

          <p className="text-xs text-slate-400 text-center">
            Al enviar, aceptas nuestros terminos y condiciones.
            CrediAcceso no garantiza la aprobacion del credito.
          </p>
        </form>
      </div>
    </div>
  );
}
